import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { GamePhase, GameplayAnswer, QuestionConfig, QuestionSet, Team } from '../types/GameplayTypes';
import { Log } from '../util/Logging';
import { allAnswersRevealed, isBestAnswer, opponentOf, roundPoints } from './GameplayStateUtils';
import { Assert } from './GameplayValidation';

export type GameplayQuestion = Omit<QuestionConfig, 'answers'> & {
	answers: GameplayAnswer[];
};


// Is this even needed? We have controllingTeam and answer reveal state
type FaceOffState = {
	questionVisible: boolean,
	previousAttempt: {
		team: Team;
		points: number;
	} | undefined;
};

export type GameplayState = {
	/** All questions. Normalized, so can assume questions[0] is best answer. */
	questions: GameplayQuestion[];

	phase: GamePhase;
	/** Round number. Zero-indexed. */
	round: number;
	teamScores: [number, number];
	/** Team currently in control of the board. Undefined until first team buzzes in face-off */
	controllingTeam: Team | undefined;

	/** Strike count (wrong answers) */
	strikes: number;
	maxStrikes: number;

	faceOff: FaceOffState;

	/** Current error message to display, if any */
	error: string | undefined;

	/** Analytics data */
	analytics: {
		/** Timestamp when game started (startGame action dispatched) */
		gameStartTime: number | undefined;
		/** Total number of correct answers across all rounds */
		totalCorrectAnswers: number;
		/** Total number of strikes across all rounds */
		totalStrikes: number;
		/** Team that won the current/last round */
		lastRoundWinner: Team | undefined;
	};
};

const initialFaceOff: FaceOffState = {
	questionVisible: false,
	previousAttempt: undefined,
};

const initialState: GameplayState = {
	phase: 'setup',
	round: 0,
	questions: [],
	teamScores: [0, 0],
	controllingTeam: undefined,
	strikes: 0,
	maxStrikes: 3,
	faceOff: initialFaceOff,
	error: undefined,
	analytics: {
		gameStartTime: undefined,
		totalCorrectAnswers: 0,
		totalStrikes: 0,
		lastRoundWinner: undefined,
	},
};

export const gamePlayStore = createSlice({
	name: 'gamePlay',
	initialState,
	reducers: {
		startGame: (state, action: PayloadAction<QuestionSet>) => {
			state.questions = action.payload.questions
				.filter(question => question.isActive)
				.map(config => ({
					...config,
					answers: config.answers.map(answer => ({ ...answer, revealed: false })),
				}));
			state.teamScores = [...initialState.teamScores];
			state.round = 0;
			state.analytics = {
				gameStartTime: dayjs().valueOf(),
				totalCorrectAnswers: 0,
				totalStrikes: 0,
				lastRoundWinner: undefined,
			};
			resetRound(state);
		},

		faceOffBuzz: (state, action: PayloadAction<{ team: Team; }>) => {
			Assert.phase(state, action, 'faceoff');
			Assert.condition(state, action, state.controllingTeam === undefined, `Team ${state.controllingTeam} already has control`);
			Assert.condition(state, action, state.faceOff.questionVisible, 'Question must be revealed before buzzing in');

			state.controllingTeam = action.payload.team;
		},

		playOrPass: (state, action: PayloadAction<'play' | 'pass'>) => {
			Assert.phase(state, action, 'playOrPass');
			const currentTeam = Assert.controlTeam(state, action);

			if (action.payload === 'pass') {
				state.controllingTeam = opponentOf(currentTeam);
			}
			state.phase = 'answering';
		},

		answer: (state, action: PayloadAction<{ answerId: number; }>) => {
			switch (state.phase) {
			case 'faceoff':
				faceOffAnswer(state, action);
				break;
			case 'steal':
				stealAnswer(state, action);
				break;
			case 'reveal':
				revealAnswer(state, action);
				break;
			default: {
				Assert.phase(state, action, 'answering');
				const team = Assert.controlTeam(state, action);
				const question = Assert.question(state, action, state.round);
				const answer = Assert.answer(state, action, action.payload.answerId);

				if (!answer.revealed) {
					answer.revealed = true;
					state.analytics.totalCorrectAnswers += 1;

					if (allAnswersRevealed(question)) {
						awardRound(state, team, action);
					}
				} else {
					// Don't throw as it would be easy for facilitator to hit wrong number key
					Log.game.warn(`Answer ${action.payload.answerId} already revealed`);
				}
			} break;
			}
		},

		wrongAnswer: (state, action: PayloadAction) => {
			if (state.phase === 'faceoff') {
				faceOffWrongAnswer(state, action);
			} else if (state.phase === 'steal') {
				stealWrongAnswer(state, action);
			} else {
				Assert.phase(state, action, 'answering');
				const team = Assert.controlTeam(state, action);
				Assert.condition(state, action, state.strikes < state.maxStrikes, `Already reached ${state.maxStrikes}, ${state.strikes}`);

				state.strikes = state.strikes + 1;
				state.analytics.totalStrikes += 1;
				if (state.strikes >= state.maxStrikes) {
					state.phase = 'steal';
					state.controllingTeam = opponentOf(team);
				}
			}
		},

		revealQuestion: (state, action: PayloadAction) => {
			Assert.phase(state, action, 'faceoff');
			state.faceOff.questionVisible = true;
		},
		revealAll: (state, action: PayloadAction) => {
			Assert.phase(state, action, 'reveal');
			const question = Assert.question(state, action);
			question.answers = question.answers.map(a => ({ ...a, revealed: true }));
			state.phase = 'roundEnd';
		},

		nextRound: (state, _action: PayloadAction) => {
			if (state.round < state.questions.length - 1) {
				state.round += 1;
				resetRound(state);
			} else {
				state.phase = 'gameEnd';
			}
		},

		adjustScore: (state, action: PayloadAction<{ team: Team; score: number; }>) => {
			state.teamScores[action.payload.team] = action.payload.score;
		},

		switchTeam: (state, _action: PayloadAction) => {
			if (state.controllingTeam !== undefined) {
				state.controllingTeam = opponentOf(state.controllingTeam);
			}
		},

		restartGame: (state, _action: PayloadAction) => {
			state.teamScores = [...initialState.teamScores];
			state.round = 0;
			resetRound(state);
		},

		resetGameplay: () => {
			return initialState;
		},

		clearStrikes: (state) => { state.strikes = 0; },

		setError: (state, action: PayloadAction<string>) => {
			state.error = action.payload;
		},

		clearError: (state) => {
			state.error = undefined;
		},
	},
	selectors: {
		phase: (state) => state.phase,
		questions: (state) => state.questions,
		currentQuestion: (state) => state.questions[state.round],
		questionVisible: (state) => (state.phase !== 'faceoff') || state.faceOff.questionVisible,
		round: (state) => state.round,
		teamScores: (state) => state.teamScores,
		roundPoints: (state) => roundPoints(state),
		strikes: (state) => ({ strikes: state.strikes, maxStrikes: state.maxStrikes }),
		controllingTeam: (state) => state.controllingTeam,

		stealingTeam: (state) => (state.controllingTeam === undefined
			? undefined
			: opponentOf(state.controllingTeam)),

		faceOffAttemptTeam: (state) => state.faceOff.previousAttempt
			? opponentOf(state.faceOff.previousAttempt.team)
			: undefined,

		analytics: (state) => state.analytics,
		error: (state) => state.error,
	},
});

export const useGameplaySelector = useSelector.withTypes<{ gamePlay: GameplayState; }>();

/** Clears everything that's specific to a single round, leaving scores and questions untouched */
function resetRound(state: GameplayState) {
	state.strikes = 0;
	state.controllingTeam = undefined;
	state.phase = 'faceoff';
	state.faceOff = initialFaceOff;
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	state.questions[state.round]!.answers = state.questions[state.round]!.answers.map(a => ({ ...a, revealed: false }));
}

function faceOffAnswer(state: GameplayState, action: PayloadAction<{ answerId: number; }>) {
	const { answerId } = action.payload;
	const { previousAttempt } = state.faceOff;
	const previousAnswerWasCorrect = (previousAttempt?.points ?? 0) > 0;

	Assert.phase(state, action, 'faceoff');
	const team = Assert.faceOffTeam(state, action);
	const question = Assert.question(state, action);

	// Correct answer
	const answer = Assert.answer(state, action, answerId);
	Assert.condition(state, action, !answer.revealed, 'Answer already revealed');
	answer.revealed = true;
	state.analytics.totalCorrectAnswers += 1;

	if (isBestAnswer(question, answerId)) {
		// Got top answer, move onto playOrPass
		state.controllingTeam = team;
		state.faceOff = initialFaceOff;
		state.phase = 'playOrPass';
	} else if (previousAnswerWasCorrect) {
		// Two correct answers given. Compare to find who takes control
		state.controllingTeam = (answer.points > (previousAttempt?.points ?? 0))
			? team
			: opponentOf(team);
		state.faceOff = initialFaceOff;
		state.phase = 'playOrPass';
	} else {
		// First correct answer but not the best
		if (previousAttempt) {
			// Other team already answered incorrectly - This team wins
			state.controllingTeam = team;
			state.faceOff = initialFaceOff;
			state.phase = 'playOrPass';
		} else {
			// Other team hasn't answered yet, give them a chance
			state.faceOff.previousAttempt = { team, points: answer.points };
			state.controllingTeam = opponentOf(team);
		}
	}
}

function faceOffWrongAnswer(state: GameplayState, action: PayloadAction) {
	const { previousAttempt } = state.faceOff;
	const previousAnswerWasCorrect = (previousAttempt?.points ?? 0) > 0;

	Assert.phase(state, action, 'faceoff');
	const team = Assert.faceOffTeam(state, action);

	if (previousAnswerWasCorrect) {
		state.controllingTeam = previousAttempt?.team;
		state.faceOff = initialFaceOff;
		state.phase = 'playOrPass';
	} else {
		// TODO: Is this correct? Not defined in 'How to play' doc. Ask Joel.
		// For now, just keep alternating
		state.faceOff.previousAttempt = { team, points: 0 };
		state.controllingTeam = opponentOf(team);
	}
}

function stealAnswer(state: GameplayState, action: PayloadAction<{ answerId: number; }>) {
	Assert.phase(state, action, 'steal');
	const team = Assert.controlTeam(state, action);
	const answer = Assert.answer(state, action, action.payload.answerId);

	if (!answer.revealed) {
		answer.revealed = true;
		state.analytics.totalCorrectAnswers += 1;
		awardRound(state, team, action);
	}
}

function stealWrongAnswer(state: GameplayState, action: PayloadAction<unknown>) {
	Assert.phase(state, action, 'steal');
	const team = Assert.controlTeam(state, action);

	awardRound(state, opponentOf(team), action);
}

function awardRound(state: GameplayState, team: Team, action: PayloadAction<unknown>) {
	const question = Assert.question(state, action);

	state.teamScores[team] += roundPoints(state);
	state.analytics.lastRoundWinner = team;
	const hasUnrevealedAnswers = question.answers.some(a => !a.revealed);
	state.phase = hasUnrevealedAnswers ? 'reveal' : 'roundEnd';
	state.controllingTeam = undefined;
}

function revealAnswer(state: GameplayState, action: PayloadAction<{ answerId: number; }>) {
	Assert.phase(state, action, 'reveal');
	const question = Assert.question(state, action);
	const answer = Assert.answer(state, action, action.payload.answerId);

	if (!answer.revealed) {
		answer.revealed = true;
		if (allAnswersRevealed(question)) {
			state.phase = 'roundEnd';
		}
	}
}